import type { CityConfig } from "@/pages/city-template/types";

export const tualatinConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Washington County Clerk Vital Records", desc: "Obtain vital records through the Washington County Clerk's office.", url: "https://www.washingtoncountyor.gov/CountyClerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Tualatin",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "tualatin",
  route: "/tualatin",
  title: "Emergency Passport Tualatin, OR | Expedited Passport Renewal & Rush Services",
  metaDescription: "Emergency passport and expedited passport services in Tualatin, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Tualatin and south metro Oregon travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Tualatin, Oregon",
  heroSubtitle: "Flying out of PDX for a wine country tour or international trip? Passport expired or lost? Fast Passport Center helps Tualatin travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Tualatin%20Oregon%20suburban%20landscape%20with%20modern%20business%20parks%20and%20green%20Pacific%20Northwest%20trees%2C%20Tualatin%20River%20visible%20in%20background%2C%20clean%20contemporary%20architecture%2C%20warm%20natural%20daylight%2C%20photorealistic%20suburban%20Oregon%20scenery%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=tualatin-hero-01&orientation=landscape",
  heroImageAlt: "Tualatin Oregon suburban landscape with modern business parks and green trees",
  geoPosition: "45.3840;-122.7609",
  geoPlacename: "Tualatin, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 180 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Tualatin", zips: "97062" },
    { name: "Wilsonville", zips: "97070" },
    { name: "Newberg", zips: "97132" },
    { name: "Sherwood", zips: "97140" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Beaverton", zips: "97002–97078" },
    { name: "Canby", zips: "97013" },
    { name: "Oregon City", zips: "97045" },
    { name: "West Linn", zips: "97068" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Aurora", zips: "97002" },
    { name: "Hubbard", zips: "97032" },
    { name: "Woodburn", zips: "97071" },
    { name: "Donald", zips: "97020" },
    { name: "Charbonneau", zips: "97070" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Tualatin in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Tualatin", desc: "FedEx and UPS locations throughout Tualatin and Wilsonville offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Michelle D.",
      location: "Tualatin, OR",
      rating: 5,
      text: "Wine tour in France planned for months and my passport was expired. Fast Passport Center got it renewed in 5 days. I shipped from the FedEx on Boones Ferry Road. The whole process was incredibly smooth.",
      service: "Passport Renewal",
    },
    {
      name: "Tom S.",
      location: "Wilsonville, OR",
      rating: 5,
      text: "Tech conference in Barcelona with two weeks notice. Passport had less than 3 months validity. Fast Passport Center handled the expedited renewal and I had it back in 4 days. The daily email updates were reassuring.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer W.",
      location: "Tualatin, OR",
      rating: 5,
      text: "Our twins needed passports for a school trip to England. Fast Passport Center made the child passport process straightforward — both parents signed, acceptance clerk visit in Oregon City, and we had both passports in 7 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19235 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Tualatin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient passport acceptance in downtown Tualatin. Appointment required — book online well in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "tigard-post",
      name: "Tigard Post Office",
      type: "USPS Passport Acceptance",
      address: "11930 SW Greenburg Rd",
      city: "Tigard",
      state: "OR",
      zip: "97223",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tigard — about 3 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tigard option for northern Tualatin residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "wilsonville-post",
      name: "Wilsonville Post Office",
      type: "USPS Passport Acceptance",
      address: "29399 SW Town Center Loop E",
      city: "Wilsonville",
      state: "OR",
      zip: "97070",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Wilsonville — about 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wilsonville option for southern Tualatin residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "620 3rd St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 8 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Clackamas County option for eastern Tualatin area. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county",
      name: "Clackamas County Clerk",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Oregon City — about 8 miles east",
      website: "https://www.clackamas.us/clerk",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk passport acceptance for Clackamas County residents. Often faster scheduling than USPS. Appointment required.",
      icon: "ri-government-line",
    },
    {
      id: "beaverton-post",
      name: "Beaverton Post Office",
      type: "USPS Passport Acceptance",
      address: "4265 SW 106th Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Beaverton — about 10 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Beaverton option for western Tualatin residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Tualatin before international travel?",
      a: "Yes. Through Fast Passport Center, Tualatin travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in Tualatin?",
      a: "Fast passport renewal in Tualatin is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my wine country trip to France?",
      a: "If your passport expires before your wine country trip to France or Italy, act immediately. European countries typically require your passport to be valid for at least 3–6 months beyond your travel dates. Fast Passport Center can expedite your passport renewal so you don't miss your reservations. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near Tualatin?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Tualatin, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Tualatin?",
      a: "Yes. Child passport expediting in Tualatin is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Tualatin have a passport agency?",
      a: "Tualatin, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 180 miles north) and the San Francisco Passport Agency in California (about 635 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Tualatin residents.",
    },
    {
      q: "How long does a standard passport take in Tualatin?",
      a: "If you apply through a standard post office acceptance facility in Tualatin or nearby Portland, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Tualatin travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Tualatin without visiting an office?",
      a: "Yes. Most Tualatin-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in the Portland metro area.",
    },
    {
      q: "What airports do Tualatin passport holders typically fly from?",
      a: "Portland International Airport (PDX) is the primary airport for Tualatin travelers, located about 15 miles north. PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Salem (SLE) and Eugene (EUG) are regional alternatives. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Tualatin", address: "19251 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Wilsonville", address: "30040 SW Boones Ferry Rd, Wilsonville, OR 97070", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Tualatin", address: "19235 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Tigard", address: "11527 SW Pacific Hwy, Tigard, OR 97223", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Tigard", address: "16275 SW Pacific Hwy, Tigard, OR 97224", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Wilsonville", address: "25700 SW Heather Pl, Wilsonville, OR 97070", hours: "Daily 7 AM – 11 PM" },
    { name: "Costco — Tigard", address: "7855 SW Dartmouth St, Tigard, OR 97223", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Portland", address: "908 SW 4th Ave, Portland, OR 97204", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Beaverton", address: "1590 NW 185th Ave, Beaverton, OR 97006", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Sherwood", address: "16335 SW Langer Dr, Sherwood, OR 97140", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Tualatin DMV Office",
      address: "14799 SE 82nd Dr, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Nearest DMV for Tualatin residents. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Oregon City DMV Office",
      address: "16318 S Beavercreek Rd, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "South Clackamas County option. Appointment recommended.",
      featured: false,
    },
    {
      name: "Beaverton DMV Office",
      address: "14075 SW Barrows Rd, Beaverton, OR 97007",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Washington County option for western Tualatin residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 15 miles north",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 180 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Tualatin travelers use SEA for additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Tualatin and the south metro area.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Tualatin and south metro Oregon travelers renew fast — passport expired before trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Tualatin", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Tualatin travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Tualatin families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Tualatin residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Tualatin travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Tualatin, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Tualatin residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Tualatin, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Tualatin travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Tualatin families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Tualatin, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Tualatin travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your wine tour or trip to Europe, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Tualatin." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Tualatin runs 6–13 weeks. If your trip to Europe, Asia, or the Caribbean is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Tualatin travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Tualatin", description: "Drop off at any FedEx or UPS in the Tualatin or Portland metro area — most locations are open evenings and weekends. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Tualatin" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Tigard", route: "/tigard" },
    { name: "Emergency passport help near Lake Oswego", route: "/lake-oswego" },
    { name: "Rush passport service in Wilsonville", route: "#" },
    { name: "Fast passport renewal for south metro Oregon travelers", route: "/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Tualatin or the Portland metro area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Tualatin and all of south metro Oregon remotely",
  serviceAreaIntro: "Whether you are in Tualatin, Wilsonville, Newberg, Sherwood, or anywhere in the south Portland metro area, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Tualatin and south metro Oregon zip codes. Ship overnight from any FedEx or UPS in the Portland metro area and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Tualatin as simple as possible. Most Tualatin clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Tualatin travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Tualatin, Wilsonville, and south metro Oregon travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Tualatin and south metro Oregon travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};